import type { LightEntry, PageFields } from '@beat-venues/common-types';
import {
    Options,
    documentToReactComponents,
} from '@contentful/rich-text-react-renderer';
import {
    BLOCKS,
    INLINES,
    MARKS,
    type Block,
    type Document,
    type Inline,
} from '@contentful/rich-text-types';
import { ReactNode, type JSX } from 'react';

import { Heading, Paragraph, Button, TextLink } from '../../';
import { useTranslation } from 'react-i18next';
import type { CommonProps } from '../../types';

export interface RichTextProps extends CommonProps, GetOptionsParams {
    body: Document;
}

interface GetOptionsParams {
    paragraphSize?: 'lg' | 'md' | 'sm';
    disableMultiline?: boolean;
    disableHeadings?: boolean;
    disableLists?: boolean;
    disableHr?: boolean;
    disableTextLink?: boolean;
    renderEmbeddedEntries?: (
        node: Block | Inline,
        children: ReactNode
    ) => JSX.Element;
}

const isRecord = (obj: unknown): obj is Record<string, unknown> =>
    typeof obj === 'object';

const isValidPageEntry = (node: unknown): node is LightEntry<PageFields> => {
    if (typeof node !== 'object' || !isRecord(node)) {
        return false;
    }

    return (
        Object.keys(node).includes('sys') &&
        Object.keys(node).includes('fields')
    );
};

const getOptions = (params: GetOptionsParams, locale: string): Options => {
    return {
        renderMark: {
            [MARKS.BOLD]: (text: ReactNode) => (
                <b className="font-bold">{text}</b>
            ),
            [MARKS.UNDERLINE]: (text: ReactNode) => text,
        },
        renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children) => {
                return (
                    <Paragraph
                        className={params.disableMultiline ? 'mb-0' : ''}
                        size={params.paragraphSize}
                    >
                        {children}
                    </Paragraph>
                );
            },
            [BLOCKS.HEADING_2]: (node, children) =>
                !params.disableHeadings && <Heading>{children}</Heading>,
            [BLOCKS.HEADING_3]: (node, children) =>
                !params.disableHeadings && (
                    <Heading as="h3">{children}</Heading>
                ),
            [BLOCKS.HEADING_4]: (node, children) =>
                !params.disableHeadings && (
                    <Heading as="h4">{children}</Heading>
                ),
            [BLOCKS.HEADING_5]: (node, children) =>
                !params.disableHeadings && (
                    <Heading as="h5">{children}</Heading>
                ),
            [BLOCKS.HEADING_6]: (node, children) =>
                !params.disableHeadings && (
                    <Heading as="h6">{children}</Heading>
                ),
            [BLOCKS.HR]: () => !params.disableHr && <hr className="mb-4" />,
            [BLOCKS.UL_LIST]: (node, children) =>
                !params.disableLists && (
                    <ul className="mb-4 list-disc pl-8">{children}</ul>
                ),
            [BLOCKS.OL_LIST]: (node, children) =>
                !params.disableLists && (
                    <ol className="mb-4 list-decimal pl-8">{children}</ol>
                ),
            [BLOCKS.LIST_ITEM]: (node) => {
                const defaultOptions = getOptions(params, locale);
                return documentToReactComponents(node as Document, {
                    ...defaultOptions,
                    renderNode: {
                        ...defaultOptions.renderNode,
                        [BLOCKS.LIST_ITEM]: (node, children) => (
                            <li className="mb-4 text-base font-light leading-6">
                                {children}
                            </li>
                        ),
                    },
                });
            },
            [INLINES.ENTRY_HYPERLINK]: (node, children) => {
                if (!isValidPageEntry(node.data['target'])) {
                    return children;
                }

                if (params.disableTextLink) {
                    return <Button>{children}</Button>;
                }

                return (
                    <TextLink
                        className={params.disableMultiline ? 'text-black' : ''}
                        size="none"
                        weight="inherit"
                    >
                        {children}
                    </TextLink>
                );
            },
            [INLINES.HYPERLINK]: (node, children) => {
                if (params.disableTextLink) {
                    return <Button href={node.data['uri']}>{children}</Button>;
                }
                return (
                    <TextLink
                        className={params.disableMultiline ? 'text-black' : ''}
                        href={node.data['uri']}
                        size="none"
                        weight="inherit"
                    >
                        {children}
                    </TextLink>
                );
            },
            [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
                if (!params.renderEmbeddedEntries) {
                    return;
                }
                return params.renderEmbeddedEntries(node, children);
            },
        },
        renderText: (text: string) => {
            if (params.disableMultiline) {
                return text.split('\n');
            }
            return text
                .split('\n')
                .reduce((children: ReactNode[], textSegment, index) => {
                    return [
                        ...children,
                        index > 0 && <br key={index} />,
                        textSegment,
                    ];
                }, []);
        },
    };
};

export const RichText = ({
    className,
    body,
    paragraphSize,
    disableMultiline,
    disableHeadings,
    disableLists,
    disableHr,
    disableTextLink,
    testId = 'rich-text',
    renderEmbeddedEntries,
    ...props
}: RichTextProps): JSX.Element => {
    const { i18n } = useTranslation();
    const currentLocale = i18n.language;

    return (
        <div className={className ?? undefined} data-testid={testId} {...props}>
            {documentToReactComponents(
                body,
                getOptions(
                    {
                        paragraphSize,
                        disableMultiline,
                        disableHeadings,
                        disableLists,
                        disableHr,
                        disableTextLink,
                        renderEmbeddedEntries,
                    },
                    currentLocale
                )
            )}
        </div>
    );
};
